import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import styles from '../Auth.module.css';

/**
 * VerificationCodeInput - 6-digit OTP input with auto-focus and paste support
 */
const VerificationCodeInput = ({
  value = ['', '', '', '', '', ''],
  onChange,
  disabled = false,
  error = false,
}) => {
  const inputRefs = useRef([]);

  // Handle single digit input
  const handleChange = (index, inputValue) => {
    // Only allow digits
    if (!/^\d*$/.test(inputValue)) return;

    const newCode = [...value];
    newCode[index] = inputValue.slice(-1); // Only take last character
    onChange(newCode);

    // Auto-focus next input
    if (inputValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle key events
  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace if current is empty
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    
    // Move to next input on right arrow
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Move to previous input on left arrow
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste event
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    
    // Check if pasted content is 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      onChange(newCode);
      inputRefs.current[5]?.focus();
    }
  };

  return (
    <div className={styles['au-code-container']}>
      {value.map((digit, index) => (
        <motion.input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={index === 0 ? handlePaste : undefined}
          className={`
            ${styles['au-code-input']}
            ${digit ? styles['au-code-input--filled'] : ''}
          `.trim()}
          disabled={disabled}
          autoFocus={index === 0}
          autoComplete="off"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.05 }}
        />
      ))}
    </div>
  );
};

export default VerificationCodeInput;
